package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.LocalNavDismiss
import co.maytes.standupbot.dashboard.components.layout.NavPillHost
import co.maytes.standupbot.dashboard.components.layout.NavRowLink
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideArrowLeft
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideLock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSearch
import co.maytes.standupbot.dashboard.styles.ControlHeight
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

@Composable
fun SettingsSidebar(
    sections: List<SettingsSection>,
    activeId: String,
    attentionId: String?,
    onSelect: (String) -> Unit,
    onSearch: () -> Unit,
) {
    val dismiss = LocalNavDismiss.current
    Column(modifier = Modifier.fillMaxWidth()) {
        NavRowLink(
            label = "Back to app",
            active = false,
            path = "/app",
            icon = { color -> LucideArrowLeft(size = ICON_SIZE, strokeColor = color) },
        )
        SpanText(
            text = "Settings",
            modifier = Modifier
                .padding(leftRight = Spacing.sm)
                .margin(top = Spacing.sm, bottom = Spacing.xs)
                .fontSize(Typography.h4.size)
                .fontWeight(600)
                .color(Palette.textPrimary)
                .styleModifier { property("letter-spacing", "-0.01em") },
        )
        SearchTrigger(onSearch)
        NavPillHost(activeKey = activeId, layoutKey = sections.size) {
            SettingsGroup.entries.forEach { group ->
                val items = sections.filter { it.group == group }
                if (items.isEmpty()) return@forEach
                GroupHeader(group)
                items.forEach { section ->
                    NavRowLink(
                        label = section.label,
                        active = section.id == activeId,
                        icon = section.icon,
                        needsAttention = section.id == attentionId,
                        onClick = {
                            onSelect(section.id)
                            dismiss()
                        },
                    )
                }
            }
        }
    }
}

private const val ICON_SIZE = 16

@Composable
private fun GroupHeader(group: SettingsGroup) {
    Row(
        modifier = Modifier
            .padding(leftRight = Spacing.sm)
            .margin(top = Spacing.lg, bottom = Spacing.xxs)
            .attrsModifier {
                if (group.adminOnly) attr("title", "Admins and owners only")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = group.label,
            modifier = Modifier.fontSize(Typography.caption.size).fontWeight(400).color(Palette.textTertiary),
        )
        if (group.adminOnly) {
            Box(modifier = Modifier.margin(left = Spacing.xxs).color(Palette.textTertiary)) {
                LucideLock(size = LOCK_SIZE)
            }
        }
    }
}

private const val LOCK_SIZE = 12

@Composable
private fun SearchTrigger(onSearch: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(ControlHeight.md)
            .margin(bottom = Spacing.xxs)
            .padding(leftRight = Spacing.sm)
            .borderRadius(Radius.md)
            .cursor(Cursor.Pointer)
            .role("button")
            .tabIndex(0)
            .onClick { onSearch() }
            .onKeyDown { event ->
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    onSearch()
                }
            }
            .attrsModifier { attr("aria-label", "Search settings") }
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("gap", Spacing.xs.toString())
                property("box-sizing", "border-box")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(modifier = Modifier.color(Palette.textTertiary)) { LucideSearch(size = SEARCH_ICON) }
        SpanText(
            text = "Search settings",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(400)
                .color(Palette.textTertiary)
                .styleModifier { property("flex", "1 1 auto") },
        )
        SpanText(
            text = "/",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary)
                .padding(leftRight = Spacing.xs)
                .borderRadius(Radius.inner)
                .styleModifier { property("border", "1px solid ${Palette.border.cssRgb()}") },
        )
    }
}

private const val SEARCH_ICON = 15
